import React, { useContext, useMemo, useState } from "react"
import moment from "moment"
import { Sparkles } from "lucide-react"

import { ModalContext } from "../../../utils/providers/modal"
import Button from "../../../components/ui/button/button"

import ParseWindow from "./parse-window"
import { persistWebsiteEnquiryParse, runParse } from "./run-parse"
import { FieldsCompletedCell, ParseStatusBadge } from "./row-cells"

/**
 * Single row for the website-enquiries tab. Mirrors the email-queue row UX:
 *
 *   - Click the row body → open the modal showing the saved parse result
 *     (or empty state if never parsed).
 *   - Click the Parse / Reparse button → fire the parser inline with loading
 *     shown on the button. No modal opens.
 *
 * Parse results are persisted under `enquiry.ai_parsing` on the source
 * enquiry doc — never touching any other field. The parent tab's snapshot
 * listener carries the new state into this row, so there's no in-memory
 * cache to maintain.
 */
function QueueWebsiteRow({ enquiry, client }) {
	const { showModal } = useContext(ModalContext)
	const [showingParse, setShowingParse] = useState(false)
	const [parsing, setParsing] = useState(false)

	// Source of truth for the parse state is `enquiry.ai_parsing` on the
	// enquiry doc itself — written by handleParse below and surfaced here via
	// the parent tab's onSnapshot listener. No in-memory cache needed.
	const aiParsing = enquiry.ai_parsing || null
	const parsedResult = aiParsing?.result || null
	const parsedSettings = aiParsing?.settings || null

	const createdAt = enquiry.created?.seconds
		? moment.unix(enquiry.created.seconds).format("D MMM YYYY, HH:mm")
		: null

	const parsedAt = aiParsing?.parsed_at?.seconds
		? moment.unix(aiParsing.parsed_at.seconds).format("D MMM YYYY, HH:mm")
		: null

	const clientName = client
		? [client.first_name, client.last_name].filter(Boolean).join(" ")
		: ""

	// Synthetic email-shaped payload the parser expects. Stringifies the
	// structured website-enquiry fields into a body the parser can read with
	// the existing prompt; UA / timezone / UTM hints from `enquiry.tracking`
	// are written explicitly so the parser's client_signals + utm_tracking
	// rules can lift them.
	const inlinePayloadEntry = useMemo(() => {
		const tracking = enquiry.tracking || {}
		const metadata = enquiry.metadata || {}
		const lines = []

		lines.push(`[Website enquiry — ${enquiry.site || "unknown site"}]`)
		if (clientName) lines.push(`Submitted by: ${clientName}`)
		if (client?.email) lines.push(`Email: ${client.email}`)
		if (client?.phone) lines.push(`Phone: ${client.phone}`)
		if (enquiry.location) lines.push(`Destination: ${enquiry.location}`)
		if (enquiry.date_from?.seconds) {
			lines.push(
				`Travel from: ${moment.unix(enquiry.date_from.seconds).format("YYYY-MM-DD")}`
			)
		}
		if (enquiry.nights) lines.push(`Nights: ${enquiry.nights}`)
		if (enquiry.rounds) lines.push(`Rounds: ${enquiry.rounds}`)
		if (enquiry.group_size) lines.push(`Group size: ${enquiry.group_size}`)

		if (enquiry.notes) {
			lines.push("")
			lines.push("Notes from customer:")
			lines.push(enquiry.notes)
		}

		const trackingLines = []
		const trackingKeys = [
			"utm_source",
			"utm_medium",
			"utm_campaign",
			"utm_term",
			"utm_content",
		]
		for (const key of trackingKeys) {
			if (tracking[key]) trackingLines.push(`${key}: ${tracking[key]}`)
		}
		if (metadata.user_agent) trackingLines.push(`user_agent: ${metadata.user_agent}`)
		if (metadata.user_time_zone) trackingLines.push(`timezone: ${metadata.user_time_zone}`)
		if (trackingLines.length > 0) {
			lines.push("")
			lines.push("Tracking signals:")
			for (const line of trackingLines) lines.push(line)
		}

		const body = lines.join("\n")

		// No `id` here — `runParse` only persists when persist=true AND id is
		// set, so passing this through is inherently transient.
		return {
			subject: clientName ? `Website enquiry from ${clientName}` : "Website enquiry",
			from_name: clientName,
			from_address: client?.email || "",
			body,
			body_content_type: "text",
			received_at_iso: enquiry.created?.toDate
				? enquiry.created.toDate().toISOString()
				: undefined,
		}
	}, [enquiry, client, clientName])

	const handleParse = async () => {
		setParsing(true)
		try {
			const { data, settings } = await runParse(inlinePayloadEntry, {
				persist: false,
			})
			// Persist into the namespaced ai_parsing map on the enquiry doc.
			// The parent's snapshot listener picks the change up automatically
			// and re-renders this row, so there's no separate state to update.
			await persistWebsiteEnquiryParse(enquiry.id, data, settings)
		} catch (err) {
			showModal({
				type: "ALERT",
				title: "Parse failed",
				body: err?.message || "Could not run parse.",
				cancel: { label: "Dismiss", action: () => null },
				next: { label: "Dismiss", action: () => null },
			})
		} finally {
			setParsing(false)
		}
	}

	const stop = (handler) => (event) => {
		event.stopPropagation()
		handler?.(event)
	}

	const hasParsedResult = !!parsedResult

	return (
		<>
			<tr
				data-system-id={enquiry.id}
				className="queue-row is-clickable"
				onClick={() => setShowingParse(true)}>
				<td>{createdAt || <small className="no-value">—</small>}</td>

				<td>{parsedAt || <small className="no-value">—</small>}</td>

				<td>{clientName || <small className="no-value">—</small>}</td>

				<td>{client?.email || <small className="no-value">—</small>}</td>

				<td>{client?.phone || <small className="no-value">—</small>}</td>

				<td className="queue-row-status-cell">
					<ParseStatusBadge
						hasParsedAt={!!aiParsing?.parsed_at}
						parsed={parsedResult}
					/>
				</td>

				<td>
					<FieldsCompletedCell
						parsed={parsedResult}
						fields={parsedSettings?.fields}
					/>
				</td>

				<td className="is-button">
					<Button
						small={true}
						label={hasParsedResult ? "Reparse" : "Parse"}
						icon={<Sparkles size={12} />}
						loading={parsing}
						loadingText="Parsing..."
						disabled={parsing}
						onClick={stop(handleParse)}
					/>
				</td>
			</tr>

			{showingParse && (
				<ParseWindow
					entry={inlinePayloadEntry}
					persist={false}
					initialResult={
						parsedResult
							? {
									output_text: aiParsing?.output_text || "",
									parsed: parsedResult,
									model_used: aiParsing?.model || null,
									usage: aiParsing?.usage || null,
							  }
							: null
					}
					initialSettings={
						parsedSettings
							? {
									fields: parsedSettings.fields || [],
									predictions: parsedSettings.predictions || [],
							  }
							: null
					}
					onParsed={({ result, settings }) =>
						persistWebsiteEnquiryParse(
							enquiry.id,
							{
								parsed: result.parsed,
								output_text: result.output_text,
								model_used: result.model_used,
								usage: result.usage,
							},
							settings
						)
					}
					close={() => setShowingParse(false)}
				/>
			)}
		</>
	)
}

export default QueueWebsiteRow
